import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Grid } from '@mui/material'

import Button from '../components/Button'
import Select from '../components/Select'
import InputField from '../components/InputField'
import GridHeader from '../components/GridHeader'
import GridCheckbox from '../components/GridCheckbox'
import { config } from '../settings/configs'
import { titlesAffiliazione as titles } from '../settings/settings'
import { useFeedback } from '../hooks/feedback-hook'
import {
  useCreateAffiliazioneMutation,
  useUpdateAffiliazioneMutation,
  useGetZoneQuery,
} from '../hooks/api-slice'
import { formatBodyField } from '../hooks/utils-functions'
import {
  setAffiliazione,
  setAffiliazioneField,
} from '../container/affiliazioni'

const Affiliazione = () => {
  const { data: zone } = useGetZoneQuery()
  const affiliazione = useSelector(state => state.affiliazioni.affiliazione)

  const [createAffiliazioneMutation, { isLoading: isCreating }] =
    useCreateAffiliazioneMutation()
  const [updateAffiliazioneMutation, { isLoading: isUpdating }] =
    useUpdateAffiliazioneMutation()

  const dispatch = useDispatch()

  const aDispatch = (field, value, toCase) =>
    dispatch(
      setAffiliazioneField({ field, value: formatBodyField(value, toCase) }),
    )

  const mode = !!affiliazione?._id ? 'update' : 'add'

  const docTitle = `${mode === 'add' ? 'Nuova' : 'Modifica'} società`
  document.title = `${config?.name} - ${docTitle}`

  const { setFeedback } = useFeedback()

  const addAffiliazione = async () => {
    const result = await createAffiliazioneMutation(affiliazione)
    if (!result?.error)
      setFeedback(titles.titleSuccessAdd, false, '', '/affiliazioni')
  }

  const updateAffiliazione = async () => {
    const arg = { id: affiliazione?._id, ...affiliazione }
    const result = await updateAffiliazioneMutation(arg)
    if (!result?.error)
      setFeedback(titles.titleSuccessUpdate, false, '', '/affiliazioni')
  }

  const confirmAffiliazione = async () => {
    if (mode === 'add') await addAffiliazione()
    if (mode === 'update') await updateAffiliazione()
  }

  // trigger functions onComponentUnmounts
  useEffect(() => {
    return () => dispatch(setAffiliazione())
  }, [dispatch])

  const containerProps = {
    container: true,
    paddingY: 2,
    rowSpacing: 2,
    columnSpacing: 5,
    alignItems: 'center',
    sx: { width: '100%' },
  }

  return (
    <Grid container paddingX={2} sx={{ width: '100%' }}>
      <GridHeader h2 text={docTitle} />
      <Grid {...containerProps}>
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='codice-societa'
            value={affiliazione?.codiceSocieta}
            onChange={i => aDispatch('codiceSocieta', i)}
            autoFocus
            label='Codice di affiliazione *'
            errorFunc={() => !affiliazione?.codiceSocieta}
            mode={mode}
          />
        </Grid>
        {mode === 'add' && (
          <Grid size={{ xs: 12, md: 6 }}>
            <InputField
              id='password'
              value={affiliazione?.password}
              onChange={i => aDispatch('password', i)}
              label='Password *'
              errorFunc={() => !affiliazione?.password}
              mode={mode}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='denominazione-societa'
            value={affiliazione?.denominazioneSocieta}
            onChange={i => aDispatch('denominazioneSocieta', i, 'upper')}
            label='Ragione sociale *'
            errorFunc={() => !affiliazione?.denominazioneSocieta}
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='nome-societa'
            value={affiliazione?.nomeSocieta}
            onChange={i => aDispatch('nomeSocieta', i, 'upper')}
            label='Nome breve *'
            errorFunc={() => !affiliazione?.nomeSocieta}
            mode={mode}
          />
        </Grid>
      </Grid>
      <Grid {...containerProps}>
        <GridHeader noCenter text='Contatti della società' />
        <Grid size={{ xs: 12, md: 3 }}>
          <InputField
            id='referente'
            value={affiliazione?.referente}
            onChange={i => aDispatch('referente', i, 'upper')}
            label='Referente'
            errorFunc={() => !affiliazione?.referente}
            adornment='person'
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <InputField
            id='ruolo'
            value={affiliazione?.ruolo}
            onChange={i => aDispatch('ruolo', i, 'upper')}
            label='Ruolo'
            adornment='work'
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <InputField
            id='telefono'
            value={affiliazione?.telefono}
            onChange={i => aDispatch('telefono', i.replace(/\s/g, ''))}
            label='Telefono'
            errorFunc={() => !affiliazione?.telefono}
            type='tel'
            adornment='phone'
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <InputField
            id='mail'
            value={affiliazione?.mail}
            onChange={i => aDispatch('mail', i.replace(/\s/g, ''), 'lower')}
            label='E-mail'
            errorFunc={() => !affiliazione?.mail}
            type='email'
            adornment='email'
            mode={mode}
          />
        </Grid>
      </Grid>
      <Grid {...containerProps}>
        <GridHeader noCenter text='Altre informazioni' />
        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            id='zona'
            value={affiliazione?.zona}
            options={zone?.map(z => z.zona)}
            optionLabels={zone?.map(z => {
              if (!z.descrizione) return z.zona
              return `${z.zona} - ${z.descrizione}`
            })}
            label='Zona geografica'
            onChange={i => aDispatch('zona', i)}
            errorFunc={() => !affiliazione?.zona}
            showEmpty
            mode={mode}
          />
        </Grid>
        <GridCheckbox
          label='Società attiva'
          value={affiliazione?.isActive !== false}
          onChange={(_, x) => aDispatch('isActive', x)}
          md={3}
          justifyContent='flex-start'
        />
      </Grid>
      <Grid {...containerProps}>
        <Grid size='grow'>
          <center>
            <Button error to='/affiliazioni' children='Indietro' />
          </center>
        </Grid>
        <Grid size='grow'>
          <center>
            <Button
              dark
              loading={isCreating || isUpdating}
              onClick={confirmAffiliazione}
            >
              Salva
            </Button>
          </center>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default Affiliazione
